import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trans, t } from "@lingui/macro";
import { DatePicker } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { observer } from "mobx-react-lite";
import type { OurWorkOverviewModel } from "../../models";
import InvoicedButton from "../InvoicedButton";
import ItemTitle, { type ItemTitleProps } from "../ItemTitle";

type Props = {
  budgetItem: OurWorkOverviewModel["budgetItems"][0];
  isProjectTemplate?: boolean;
} & Pick<ItemTitleProps, "tabIndex">;

export const OurWorkReorderItem = observer(function OurWorkReorderItem({
  budgetItem,
  tabIndex,
  isProjectTemplate,
}: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: budgetItem.id,
  });

  return (
    <Grid
      ref={setNodeRef}
      pos="relative"
      zIndex={isDragging ? 2 : 1}
      alignItems="center"
      templateColumns="minmax(0, 0.55fr) 0.2fr 0.2fr 0.05fr"
      px="4"
      py="3"
      bg="white"
      borderColor="gray.300"
      _notLast={{
        borderBottomWidth: "1px",
      }}
      transform={CSS.Transform.toString(transform)}
      transition={transition}
      data-group
      {...attributes}
    >
      <HStack spacing="2.5">
        <IconButton
          aria-label={t`Drag handle`}
          colorScheme="grey"
          size="sm"
          variant="ghost"
          {...listeners}
        >
          <Icon name="equal" />
        </IconButton>
        <ItemTitle
          item={budgetItem}
          tabIndex={tabIndex}
          canRenderDeadlineIcon={false}
          onChangeDeadline={(deadline) => {
            budgetItem.setDeadline({
              start: deadline ?? undefined,
              end: deadline ?? undefined,
            });
          }}
        />
        {!budgetItem.deadline?.start && (
          <DatePicker
            usePortal
            selected={budgetItem.deadline}
            onChange={(val) => {
              budgetItem.setDeadline({
                start: val?.start,
                end: val?.end,
              });
            }}
            trigger={
              <Button
                _groupHover={{
                  visibility: "visible",
                }}
                visibility="hidden"
                colorScheme="purple"
                leftIcon={<Icon name="plus" />}
                size="xs"
                variant="outline"
              >
                <Trans>Deadline</Trans>
              </Button>
            }
          />
        )}
      </HStack>
      <Text>
        {budgetItem.doneTaskCount}&nbsp;/&nbsp;
        {budgetItem.taskCount}
      </Text>
      <Box>
        {isProjectTemplate ? (
          <GridItem />
        ) : (
          <InvoicedButton item={budgetItem} w="fit-content" />
        )}
      </Box>
      <Flex justify="end">
        <Tooltip
          label={t`Show budget item in project detail.`}
          shouldWrapChildren
        >
          <Switch
            justifySelf="end"
            isChecked={!budgetItem.hidden}
            onChange={budgetItem.toggleHidden}
          />
        </Tooltip>
      </Flex>
    </Grid>
  );
});
